import { Box } from "@material-ui/core";
import { Button } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import { Datagrid } from "components/CustomizableDataGrid";
import { CopyableWrapper } from "components/fields/CopyableWrapper";
import { DateTimeField } from "components/fields/DateFormatField";
import { OrderReferenceField } from "components/fields/OrderReferenceField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { SsccFieldWithBarcode } from "components/fields/SsccFieldWithBarcode";
import { TargetOrBrokenPalletIconField } from "components/fields/TargetOrBrokenPalletIconField";
import { YesNoField } from "components/fields/YesNoField";
import { ForkliftIcon } from "components/icons/ForkliftIcon";
import { PalletIconField } from "components/icons/PalletIconField";
import { ScrollingWrapper } from "components/ScrollingWrapper";
import { envSettings } from "core/EnvSettings";
import { OrderStatus } from "entities/OrderStatus";
import { OrderStatusMovementAndReservationPallet } from "entities/OrderStatus";
import { PalletReservationState } from "entities/PalletReservationState";
import { PalletType } from "entities/PalletType";
import React from "react";
import {
	FunctionField,
	NumberField,
	TextField,
	useDataProvider,
	useListContext,
	useTranslate,
} from "react-admin";
import { useAsyncColumnsStorage } from "utils/hooks/useAsyncColumnsStorage";
import { useNotifyError } from "utils/notifiers/useNotifyError";
import { formatWeight } from "utils/weight";

import { BoxesButton } from "../components/BoxesButton";
import { CopyableStorageLinkChip } from "../PalletsAndBoxes/components/StorageLinkChip";
import { AddBoxButton } from "./components/AddBoxButton";
import { BoxMovementBar } from "./components/box_movements/BoxMovementBar";
import { BoxMovementModal } from "./components/box_movements/BoxMovementModal";
import { boxPickStatus, reservedColor, reservedForAnotherOrder } from "./components/Constants";
import { operationModeToggleEnum } from "./components/OperationModeToggleFilter";
import { PickingListPalletDetails } from "./components/PickingListPalletDetails";
import { ReservationSummary } from "./components/ReservationSummary";
import { ReservedChipField } from "./components/ReservedChipField";
import { ReservePalletButton } from "./components/ReservePalletButton";
import { SelectTargetButton } from "./components/SelectTargetButton";
import { useInitializeReservationSelection } from "./hooks/useInitializeReservationSelection";
import { useOrderStatuses } from "./hooks/useOrderStatuses";
import { usePickingListRecords } from "./hooks/usePickingListRecords";
import { useSelectionActionHandlers } from "./hooks/useSelectionActionHandlers";
import { useStoragePlaces } from "./hooks/useStoragePlaces";

const useStyles = makeStyles((theme) => ({
	centeredDatagrid: {
		// Center all cells except the expand column
		"& .RaDatagrid-cell:not(.RaDatagrid-expandIconCell)": {
			textAlign: "center !important",
		},
		// Center content within cells (except expand cell)
		"& .RaDatagrid-cell:not(.RaDatagrid-expandIconCell) > *": {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			width: "100%",
		},
		// Keep expand button on the left
		"& .RaDatagrid-expandIconCell": {
			textAlign: "left !important",
			width: "48px", // Fixed width for expand column
		},
		// Ensure expand button stays in place
		"& .RaDatagrid-expandIcon": {
			marginLeft: "8px",
		},
		// Center chips and buttons
		"& .MuiChip-root": {
			margin: "0 auto",
		},
		"& .MuiButton-root": {
			margin: "0 auto",
		},
		"& td": {
			textAlign: "center !important",
		},
		"& th": {
			textAlign: "center !important",
		},
		"& .MuiTableRow-root:hover": {
			boxShadow: "inset 0 0 0 9999px rgba(0, 0, 0, 0.04)",
		},
	},
}));

const pickingPalletsRowStyle = (orderId, statuses) => (record) => {
	const isPalletReservedForAnotherOrder =
		record.reserved_order_id && record.reserved_order_id !== orderId;
	const isPalletReservedForCurrentOrder = record.reserved_order_id === orderId;
	const plukkCompletedColor = statuses?.[OrderStatus.PLUKK_COMPLETED]?.color || "inherit";
	const loadingCompletedColor = statuses?.[OrderStatus.LOADING_COMPLETED]?.color || "inherit";
	const bgColor = isPalletReservedForAnotherOrder
		? reservedForAnotherOrder
		: record.loaded_into_truck
		? loadingCompletedColor
		: record.into_preparation_place
		? plukkCompletedColor
		: isPalletReservedForCurrentOrder
		? reservedColor
		: "inherit";
	return {
		height: "50px",
		backgroundColor: bgColor,
	};
};

export const PickingListGrid = (props) => {
	const classes = useStyles();
	const { data, ids, filterValues, refetch } = useListContext();
	const dataProvider = useDataProvider();
	const notifyError = useNotifyError();
	const translate = useTranslate();

	const columnsAsyncStorage = useAsyncColumnsStorage("picking-list-grid-columns-storage");

	const {
		terminalId,
		orderId,
		isSelectedViewMode,
		isReservationMode,
		operationMode,
		actionOnSelectionPallet,
		actionOnBoxMovementPick,
		setBarcodeValue,
		setBarcodeDialogOpen,
		setBarcodeTitle,
		orderStatus,
		reservation,
		boxMovement,
	} = props;

	// Destructure reservation state and actions
	const {
		selectedPalletsUI,
		handlePalletReservationSelection,
		reservationTotals,
		fetchReservationTotals,
		setSelectedPalletsUI: replaceSelectedPalletsUI,
	} = reservation;

	// Use custom hooks
	const orderStatuses = useOrderStatuses();

	const { records, viewRecords, updateRecordsOnPalletSelection } = usePickingListRecords({
		isSelectedViewMode,
		isReservationMode,
		selectedPalletsUI,
		data,
		ids,
	});

	const storagePlaces = useStoragePlaces(records);

	// Destructure box movement state and actions
	const {
		isBoxMovementMode,
		isConfirmBoxMovementMode,
		setIsConfirmBoxMovementMode,
		isSelectingTarget,
		targetPallet,
		selectTargetPallet,
		cancelBoxMovement,
		removeBoxMovement,
		createNewPallet,
		cancelNewPallet,
		boxMovements,
		isCreatingNewPallet,
		movementModalOpen,
		selectedSourcePallet,
		handleOpenMovementModal,
		handleCloseMovementModal,
		handleConfirmMovement,
		isBoxPicked,
		pickBox,
		unpickBox,
		getPickedBoxIds,
	} = boxMovement;

	const isBoxMovementAllowed = OrderStatusMovementAndReservationPallet.includes(orderStatus);

	// Setup action handlers for parent component
	useSelectionActionHandlers({
		actionOnSelectionPallet,
		handlePalletReservationSelection,
		updateRecordsOnPalletSelection,
	});

	// Initialize selectedPalletsUI based on the current records to display reserved pallets checked
	useInitializeReservationSelection({
		isReservationMode,
		ids,
		data,
		replaceSelectedPalletsUI,
	});

	React.useEffect(() => {
		if (!actionOnBoxMovementPick) return;
		actionOnBoxMovementPick.current = (pallet, box) => {
			if (box.is_moved) return boxPickStatus.MOVED;
			if (isBoxPicked(pallet.id, box.id)) return boxPickStatus.DUPLICATE;
			pickBox(pallet, box, true);
			return boxPickStatus.ADDED;
		};
	}, [actionOnBoxMovementPick, isBoxPicked, pickBox]);

	const finishBoxMovement = async () => {
		try {
			await dataProvider.create(`box-movements`, {
				data: {
					order_id: orderId,
					target_pallet_id: isCreatingNewPallet ? null : targetPallet?.id,
					box_movements: Object.entries(boxMovements).map(([pallet_id, movement]) => ({
						pallet_id: Number(pallet_id),
						...movement,
					})),
				},
			});
			cancelBoxMovement();
			refetch();
		} catch (error) {
			notifyError(error);
			console.error("Failed to save box movements:", error);
		}
	};

	const isScannerMode = operationMode === operationModeToggleEnum[0].value;
	const isManualOrCombinedMode =
		envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE || !isScannerMode;

	const gridSortable = isManualOrCombinedMode;

	// Show summary when in reservation mode OR when RESERVED filter is selected
	const showReservationSummary =
		isReservationMode || filterValues?.reservation_state == PalletReservationState.RESERVED;

	// Fetch totals when RESERVED filter is selected
	React.useEffect(() => {
		if (showReservationSummary && fetchReservationTotals) {
			fetchReservationTotals();
		}
	}, [showReservationSummary, fetchReservationTotals]);

	return (
		<>
			{showReservationSummary && (
				<ReservationSummary
					reservationTotals={reservationTotals}
					records={viewRecords}
					selectedPalletsUI={selectedPalletsUI}
					orderId={orderId}
					isStatic={!isReservationMode}
				/>
			)}
			{envSettings.REACT_APP_FF_STORAGE_MODULE && isSelectingTarget && (
				<Paper style={{ padding: "16px", marginBottom: "16px", backgroundColor: "#fff3e0" }}>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">
							{translate("tab.picking_list.box_movement.select_target_pallet_or_create_new")}
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							size="small"
							startIcon={<AddIcon />}
							onClick={createNewPallet}
						>
							{translate("tab.picking_list.box_movement.buttons.create_new_pallet")}
						</Button>
					</Box>
				</Paper>
			)}
			{envSettings.REACT_APP_FF_STORAGE_MODULE && isBoxMovementMode && (
				<BoxMovementBar
					targetPallet={targetPallet}
					boxMovements={boxMovements}
					onFinish={finishBoxMovement}
					onCancel={cancelBoxMovement}
					onRemoveMovement={removeBoxMovement}
					onCreateNewPallet={createNewPallet}
					onCancelNewPallet={cancelNewPallet}
					isCreatingNewPallet={isCreatingNewPallet}
					translate={translate}
				/>
			)}
			<BoxMovementModal
				open={movementModalOpen}
				onClose={handleCloseMovementModal}
				onConfirm={handleConfirmMovement}
				pallet={selectedSourcePallet}
				boxMovements={boxMovements}
			/>
			{/* // TODO: find solution to calc maxHeight correctly */}
			<ScrollingWrapper>
				<Datagrid
					{...props}
					className={classes.centeredDatagrid}
					data={Object.fromEntries(viewRecords.map((r) => [r.id, r]))}
					ids={viewRecords.map((r) => r.id)}
					isRowSelectable={false}
					rowClick={null}
					bulkActionButtons={false}
					storage={columnsAsyncStorage}
					expand={
						<PickingListPalletDetails
							isSelectedViewMode={isSelectedViewMode}
							isReservationMode={isReservationMode}
							isConfirmBoxMovementMode={isConfirmBoxMovementMode}
							setIsConfirmBoxMovementMode={setIsConfirmBoxMovementMode}
							selectedPalletsUI={selectedPalletsUI}
							operationMode={operationMode}
							orderId={orderId}
							translate={translate}
							terminalId={terminalId}
							storagePlaces={storagePlaces}
							orderStatus={orderStatus}
							fetchReservationTotals={fetchReservationTotals}
						/>
					}
					rowStyle={pickingPalletsRowStyle(orderId, orderStatuses)}
					reorderingSupport={true}
				>
					{isReservationMode && (
						<FunctionField
							render={(record) => (
								<ReservePalletButton
									record={record}
									selectedPalletsUI={selectedPalletsUI}
									onClick={handlePalletReservationSelection}
									updateRecordsOnPalletSelection={updateRecordsOnPalletSelection}
									orderId={orderId}
									isSelectedViewMode={isSelectedViewMode}
									operationMode={operationMode}
								/>
							)}
						/>
					)}
					<PalletIconField
						icon={ForkliftIcon}
						tooltipKey="tab.pallets.icons.high_level_pallet"
						iconStyle={{ color: "red" }}
						isVisible={(record) => !!record?.is_high_level}
					/>
					<TargetOrBrokenPalletIconField />
					<FunctionField
						label=""
						render={(record) => (
							<BoxesButton
								record={record}
								isPickMode={
									isBoxMovementMode &&
									!isReservationMode &&
									record.boxes?.length > 0 &&
									record.id !== targetPallet?.id
								}
								pickedBoxIds={getPickedBoxIds(record.id)}
								isManualOrCombinedMode={isManualOrCombinedMode}
								onPickBox={pickBox}
								onUnpickBox={unpickBox}
								setBarcodeValue={setBarcodeValue}
								setBarcodeTitle={setBarcodeTitle}
								setBarcodeDialogOpen={setBarcodeDialogOpen}
							/>
						)}
					/>
					{envSettings.REACT_APP_FF_STORAGE_MODULE &&
						isBoxMovementAllowed &&
						!isReservationMode &&
						(isSelectingTarget || isBoxMovementMode) && (
							<FunctionField
								label="Actions"
								render={(record) => (
									<>
										<SelectTargetButton
											record={record}
											orderId={orderId}
											isSelectingTarget={isSelectingTarget}
											selectTargetPallet={selectTargetPallet}
										/>
										<AddBoxButton
											record={record}
											isBoxMovementMode={isBoxMovementMode}
											onOpenMovementModal={handleOpenMovementModal}
										/>
									</>
								)}
							/>
						)}
					<ReservedChipField
						source="reserved_quantity"
						tooltip={translate("tab.picking_list.reserved_quantity")}
						style={{ backgroundColor: "#196d089f", color: "white" }}
						label={translate("tab.picking_list.reserved_quantity")}
						sortable={gridSortable}
					/>

					<ReservedChipField
						source="reserved_weight"
						formatter={(record) =>
							formatWeight(record?.reserved_weight, record?.weight_unit_code, null)
						}
						tooltip={translate("tab.picking_list.reserved_weight")}
						style={{ backgroundColor: "#196d089f", color: "white" }}
						label={translate("tab.picking_list.reserved_weight")}
						sortable={gridSortable}
					/>

					<FunctionField
						source="available_quantity"
						label={translate("tab.picking_list.available_quantity")}
						render={(record) => `${record.available_quantity}`}
						sortable={gridSortable}
					/>
					<FunctionField
						source="available_weight"
						label={translate("tab.picking_list.available_weight")}
						render={(record) => `${record.available_weight}`}
						sortable={gridSortable}
					/>
					<CopyableWrapper
						source="id"
						label={translate("tab.pallets.pallet_id")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="sscc"
						label={translate("tab.pallets.sscc")}
						sortable={gridSortable}
					>
						<SsccFieldWithBarcode
							setBarcodeValue={setBarcodeValue}
							setBarcodeTitle={setBarcodeTitle}
							setBarcodeDialogOpen={setBarcodeDialogOpen}
						/>
					</CopyableWrapper>
					<CopyableWrapper
						source="sscc_original"
						label={translate("tab.pallets.sscc_original")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="gtin"
						label={translate("tab.pallets.gtin")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="production_datetime"
						label={translate("tab.pallets.production_datetime")}
						sortable={gridSortable}
					>
						<DateTimeField />
					</CopyableWrapper>
					<CopyableWrapper
						source="expiration_datetime"
						label={translate("tab.pallets.expiration_datetime")}
						sortable={gridSortable}
					>
						<DateTimeField />
					</CopyableWrapper>
					<NumberField
						source="quantity"
						label={translate("tab.pallets.quantity")}
						sortable={gridSortable}
					/>
					<FunctionField
						source="net_weight"
						label={translate("tab.pallets.weight_net")}
						render={(record) => formatWeight(record?.net_weight, record?.weight_unit_code)}
						sortable={gridSortable}
					/>
					<SelectEnumField
						source="type"
						enumName="pallet_type"
						enumObj={PalletType}
						label={translate("tab.pallets.type")}
						sortable={gridSortable}
					/>
					<FunctionField
						label={translate(
							`tab.pallets.${
								envSettings.REACT_APP_FF_STORAGE_MODULE ? "storage_place_id" : "area_id"
							}`
						)}
						render={(record) => (
							<CopyableStorageLinkChip
								pallet={record}
								terminalId={terminalId}
								storagePlaces={storagePlaces}
							/>
						)}
					/>
					<CopyableWrapper
						source="ext_id"
						label={translate("tab.pallets.external_id")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="product_number"
						label={translate("tab.pallets.product_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="product_name"
						label={translate("tab.pallets.product_name")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<YesNoField
						source="is_t1"
						label={translate("tab.pallets.is_t1")}
						sortable={gridSortable}
					/>
					<CopyableWrapper
						source="ceel_number"
						label={translate("tab.pallets.ceel_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="batch_number"
						label={translate("tab.pallets.batch_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="lot_number"
						label={translate("tab.pallets.lot_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="packing_station"
						label={translate("tab.pallets.packing_station")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<DateTimeField
						source="reserved_datetime"
						label={translate("tab.picking_list.reserved_at")}
						sortable={gridSortable}
					/>
					<OrderReferenceField
						source="reserved_order_id"
						label={"tab.picking_list.reserved_by_order"}
					/>
					<OrderReferenceField source="order_id" label={"tab.picking_list.incoming_order"} />
					<DateTimeField
						source="completed_datetime"
						label={translate("tab.picking_list.completed_at")}
						sortable={gridSortable}
					/>
				</Datagrid>
			</ScrollingWrapper>
		</>
	);
};
